import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Play, 
  Zap,
  CheckCircle
} from 'lucide-react';
import Navigation from './Navigation';
import toast from 'react-hot-toast';

const Demo = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [demoData, setDemoData] = useState({
    symbol: 'AAPL',
    betAmount: 25,
    targetGain: 50,
    timeframe: 30,
    calculatedPosition: null
  });

  const demoSteps = [
    {
      title: "Welcome to TradeBet",
      description: "The revolutionary platform where you specify your bet and target, and our AI calculates the perfect position!",
      action: "Start Demo",
      component: "welcome"
    },
    {
      title: "Step 1: Choose Your Asset",
      description: "Select any stock or asset you want to bet on",
      action: "Select AAPL",
      component: "selectAsset"
    },
    {
      title: "Step 2: Set Your Bet Amount",
      description: "Tell us how much you want to risk",
      action: "Bet $25",
      component: "setBetAmount"
    },
    {
      title: "Step 3: Set Your Target Gain",
      description: "Tell us how much you want to win",
      action: "Target $50",
      component: "setTargetGain"
    },
    {
      title: "Step 4: Choose Timeframe",
      description: "From 30 seconds to hours - pick your timeline",
      action: "30 Minutes",
      component: "setTimeframe"
    },
    {
      title: "Step 5: AI Calculates Position",
      description: "Our sophisticated AI analyzes 10,000+ variables to find the optimal trading strategy",
      action: "Calculate Position",
      component: "calculatePosition"
    },
    {
      title: "Step 6: Review & Approve",
      description: "See the calculated position with success probability, risk analysis, and market insights",
      action: "Approve Position",
      component: "reviewPosition"
    },
    {
      title: "Demo Complete!",
      description: "You've seen how TradeBet transforms complex trading into simple betting. Ready to try it for real?",
      action: "Start Trading",
      component: "complete"
    }
  ];

  const nextStep = async () => {
    if (currentStep === 5) {
      // Simulate AI calculation
      await simulatePositionCalculation();
    }
    
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/trade');
    }
  };

  const simulatePositionCalculation = async () => {
    toast.loading('🧠 AI analyzing market conditions...', { duration: 1000 });
    
    setTimeout(() => {
      toast.dismiss();
      toast.loading('📊 Processing 10,000+ variables...', { duration: 1000 });
    }, 1000);

    setTimeout(() => {
      toast.dismiss();
      toast.loading('⚡ Calculating optimal strategy...', { duration: 1000 });
    }, 2000);

    setTimeout(() => {
      toast.dismiss();
      const mockPosition = {
        direction: 1,
        instrument: 'options',
        leverage: 3.2,
        successProbability: 67,
        riskScore: 0.35,
        confidenceScore: 0.78,
        marketAnalysis: {
          currentSentiment: 'Bullish',
          modelConfidence: 78,
          recommendedDirection: 'Long'
        }
      };
      
      setDemoData(prev => ({ ...prev, calculatedPosition: mockPosition }));
      toast.success('🎯 Position calculated successfully!');
    }, 3000);
  };

  const renderStepComponent = () => {
    const step = demoSteps[currentStep];
    
    switch (step.component) {
      case 'welcome':
        return (
          <div className="text-center">
            <div className="text-8xl mb-6">🎰</div>
            <h2 className="text-4xl font-bold text-white mb-4">Welcome to TradeBet</h2>
            <p className="text-xl text-gray-300 mb-8">
              The revolutionary platform where <span className="text-green-400 font-bold">you specify your bet and target</span>, 
              and our <span className="text-purple-400 font-bold">AI calculates the perfect position!</span>
            </p>
            <div className="bg-gray-800 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-bold text-white mb-4">How It Works:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-3xl mb-2">💰</div>
                  <div className="text-white font-medium">You Set Parameters</div>
                  <div className="text-gray-400">Asset, bet amount, target gain, timeframe</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🧠</div>
                  <div className="text-white font-medium">AI Calculates</div>
                  <div className="text-gray-400">Optimal position, leverage, success probability</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-white font-medium">You Approve</div>
                  <div className="text-gray-400">Review and confirm the calculated position</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'selectAsset':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">📈</div>
            <h3 className="text-2xl font-bold text-white mb-4">Choose Your Asset</h3>
            <div className="grid grid-cols-4 gap-4 mb-6">
              {['AAPL', 'GOOGL', 'TSLA', 'MSFT'].map((symbol) => (
                <button
                  key={symbol}
                  className={`p-4 rounded-lg font-bold transition-all ${
                    demoData.symbol === symbol
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {symbol}
                </button>
              ))}
            </div>
            <p className="text-gray-300">Selected: <span className="text-white font-bold">{demoData.symbol}</span></p>
          </div>
        );

      case 'setBetAmount':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">💰</div>
            <h3 className="text-2xl font-bold text-white mb-4">Set Your Bet Amount</h3>
            <div className="text-4xl font-bold text-green-400 mb-4">${demoData.betAmount}</div>
            <p className="text-gray-300 mb-6">This is how much you're willing to risk</p>
            <div className="flex justify-center space-x-4">
              {[10, 25, 50, 100].map((amount) => (
                <button
                  key={amount}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    demoData.betAmount === amount
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>
        );

      case 'setTargetGain':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">🎯</div>
            <h3 className="text-2xl font-bold text-white mb-4">Set Your Target Gain</h3>
            <div className="text-4xl font-bold text-green-400 mb-4">${demoData.targetGain}</div>
            <p className="text-gray-300 mb-6">This is how much you want to win</p>
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="text-sm text-gray-400 mb-2">Return Ratio</div>
              <div className="text-2xl font-bold text-yellow-400">
                {(demoData.targetGain / demoData.betAmount).toFixed(1)}x
              </div>
            </div>
          </div>
        );

      case 'setTimeframe':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">⏱️</div>
            <h3 className="text-2xl font-bold text-white mb-4">Choose Your Timeframe</h3>
            <div className="text-3xl font-bold text-blue-400 mb-4">{demoData.timeframe} Minutes</div>
            <p className="text-gray-300 mb-6">From 30 seconds to hours - pick your timeline</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { minutes: 0.5, label: '30s' },
                { minutes: 5, label: '5m' },
                { minutes: 30, label: '30m' },
                { minutes: 120, label: '2h' }
              ].map((option) => (
                <button
                  key={option.minutes}
                  className={`p-3 rounded-lg font-medium transition-all ${
                    demoData.timeframe === option.minutes
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'calculatePosition':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">🧠</div>
            <h3 className="text-2xl font-bold text-white mb-4">AI Calculating Optimal Position</h3>
            <div className="bg-gray-800 p-6 rounded-lg mb-6">
              <div className="text-lg text-white mb-4">Processing Your Parameters:</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">Asset:</div>
                  <div className="text-white font-bold">{demoData.symbol}</div>
                </div>
                <div>
                  <div className="text-gray-400">Bet Amount:</div>
                  <div className="text-white font-bold">${demoData.betAmount}</div>
                </div>
                <div>
                  <div className="text-gray-400">Target Gain:</div>
                  <div className="text-green-400 font-bold">${demoData.targetGain}</div>
                </div>
                <div>
                  <div className="text-gray-400">Timeframe:</div>
                  <div className="text-white font-bold">{demoData.timeframe}m</div>
                </div>
              </div>
            </div>
            <div className="text-gray-300">
              <div className="mb-2">🔍 Analyzing market conditions...</div>
              <div className="mb-2">📊 Processing 10,000+ variables...</div>
              <div className="mb-2">⚡ Calculating optimal strategy...</div>
            </div>
          </div>
        );

      case 'reviewPosition':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">✅</div>
            <h3 className="text-2xl font-bold text-white mb-4">Position Calculated!</h3>
            {demoData.calculatedPosition && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-bold text-white mb-4">Your Bet</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Asset:</span>
                      <span className="text-white font-bold">{demoData.symbol}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Bet Amount:</span>
                      <span className="text-white font-bold">${demoData.betAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Target Gain:</span>
                      <span className="text-green-400 font-bold">${demoData.targetGain}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-bold text-white mb-4">AI Calculated Position</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Direction:</span>
                      <span className="text-green-400 font-bold">Long ↗</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Instrument:</span>
                      <span className="text-white font-bold">Options</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Success Probability:</span>
                      <span className="text-green-400 font-bold">67%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'complete':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h3 className="text-2xl font-bold text-white mb-4">Demo Complete!</h3>
            <p className="text-xl text-gray-300 mb-8">
              You've seen how TradeBet transforms complex trading into simple betting.
            </p>
            <div className="bg-gradient-to-r from-purple-800 to-pink-800 p-6 rounded-lg mb-8">
              <h4 className="text-lg font-bold text-white mb-4">What You've Learned:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span>Simple parameter setting</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span>AI position calculation</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span>Risk analysis & probability</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span>Position approval process</span>
                </div>
              </div>
            </div>
            <p className="text-gray-300 mb-6">Ready to try it for real?</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Demo Progress</span>
            <span className="text-sm text-gray-400">{currentStep + 1} of {demoSteps.length}</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Main Demo Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          className="casino-card p-8 max-w-4xl mx-auto"
        >
          {renderStepComponent()}
          
          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              {demoSteps[currentStep].description}
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextStep}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center space-x-2"
            >
              <span>{demoSteps[currentStep].action}</span>
              {currentStep < demoSteps.length - 1 ? (
                <Play className="w-4 h-4" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Demo;
